import React, { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import {
  Search,
  Calendar,
  Users,
  Plus,
  ChevronLeft,
  ChevronRight,
  Grid,
  List,
} from "lucide-react";
import {
  Event,
  EventFilters,
  EventCategory,
  EventSeries,
  EventStatus,
} from "../../types/event";
import EventCard from "./EventCard";
import Button from "../UI/Button";
import LoadingSpinner from "../UI/LoadingSpinner";
import DateRangePicker from "../UI/DateRangePicker";
import Select from "../UI/Select";
import { eventAPI } from "../../services/eventAPI";
import { usersAPI, HostUser } from "../../services/usersAPI";
import { useAppSelector } from "../../store/hooks";
import { logError } from "../../utils/errorLogger";
import { useDebouncedCallback } from "../../hooks/useDebouncedCallback";

export interface EventListProps {
  events: Event[];
  categories?: EventCategory[];
  series?: EventSeries[];
  loading?: boolean;
  isFiltering?: boolean;
  error?: string;
  totalCount?: number;
  currentPage?: number;
  totalPages?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onFiltersChange?: (filters: EventFilters) => void;
  onEventView?: (event: Event) => void;
  onEventEdit?: (event: Event) => void;
  onEventDelete?: (event: Event) => void;
  onEventCreate?: () => void;
  onManageGuests?: (event: Event) => void;
  onApprove?: (event: Event) => void;
  onReject?: (event: Event) => void;
  showCreateButton?: boolean;
  viewMode?: "grid" | "list";
  onViewModeChange?: (mode: "grid" | "list") => void;
  userRole?: string;
  externalFilters?: EventFilters; // External filters from parent component
  onForceRefresh?: () => void; // Force refresh function from parent
}

const EventList: React.FC<EventListProps> = ({
  events,
  categories = [],
  series = [],
  loading = false,
  isFiltering = false,
  error,
  totalCount = 0,
  currentPage = 1,
  totalPages = 1,
  pageSize = 9,
  onPageChange,
  onFiltersChange,
  onEventView,
  onEventEdit,
  onEventDelete,
  onEventCreate,
  onManageGuests,
  onApprove,
  onReject,
  showCreateButton = true,
  viewMode = "grid",
  onViewModeChange,
  userRole,
  externalFilters = {},
  onForceRefresh,
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const [filters, setFilters] = useState<EventFilters>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [localViewMode, setLocalViewMode] = useState<"grid" | "list">(viewMode);
  const [locations, setLocations] = useState<string[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [dateRange, setDateRange] = useState<{
    min_date: string;
    max_date: string;
    today: string;
  } | null>(null);
  const [loadingDateRange, setLoadingDateRange] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>(""); // Local state for dropdown control
  const [hosts, setHosts] = useState<HostUser[]>([]);
  const [hostSearchTerm, setHostSearchTerm] = useState("");
  const [loadingHosts, setLoadingHosts] = useState(false);
  const [showHostDropdown, setShowHostDropdown] = useState(false);
  const [selectedHost, setSelectedHost] = useState<HostUser | null>(null);
  const [hostDropdownPosition, setHostDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const hostDropdownRef = useRef<HTMLDivElement>(null);
  const hostButtonRef = useRef<HTMLButtonElement>(null);
  const isExternalUpdate = useRef(false);
  const userJustChangedStatus = useRef(false); // Track when user just changed status
  const userJustChangedPastEvents = useRef(false); // Track when user just changed show past events
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [showPastEvents, setShowPastEvents] = useState(false); // Default: false (hide past events)

  // Constants for timing delays
  const SYNC_DELAY_MS = 1000; // Delay for sync effects to allow user changes to propagate
  const STATUS_CHANGE_DELAY_MS = 1500; // Delay for status change propagation

  // Load locations on component mount
  useEffect(() => {
    const loadLocations = async () => {
      try {
        setLoadingLocations(true);
        const locationOptions = await eventAPI.getLocationOptions();
        setLocations(locationOptions);
      } catch (error) {
        logError("Failed to load location options", error);
      } finally {
        setLoadingLocations(false);
      }
    };

    loadLocations();
  }, []);

  // Load hosts when dropdown is opened (admin only) - only if no search term
  useEffect(() => {
    if (
      showHostDropdown &&
      userRole === "admin" &&
      hosts.length === 0 &&
      !hostSearchTerm.trim()
    ) {
      if (process.env.NODE_ENV === "development") {
        // Debug: Initial load of hosts when dropdown opens
      }
      loadHosts("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showHostDropdown, userRole]);

  // Load hosts with search
  const loadHosts = async (search: string) => {
    try {
      setLoadingHosts(true);
      const hostsData = await usersAPI.getHosts(search || undefined);
      setHosts(hostsData);
    } catch (error) {
      // Failed to load hosts
      setHosts([]);
    } finally {
      setLoadingHosts(false);
    }
  };

  // Debounced version of loadHosts
  const debouncedLoadHosts = useDebouncedCallback(loadHosts, 300);

  // Handle host search with debouncing
  const handleHostSearch = (value: string) => {
    const trimmedValue = value.trim();

    // Update search term immediately for UI feedback
    setHostSearchTerm(value);

    // Clear hosts immediately when search term changes to show loading state
    setHosts([]);

    // Use debounced callback
    debouncedLoadHosts(trimmedValue);
  };

  // Handle host selection
  const handleHostSelect = (host: HostUser | null) => {
    setSelectedHost(host);
    setShowHostDropdown(false);
    setHostSearchTerm("");
    setHostDropdownPosition(null);

    if (host) {
      handleFilterChange("host_id", host.id);
    } else {
      // Use the dedicated clear function to ensure proper filter clearing
      clearHostFilter();
    }
  };

  // Calculate dropdown position when opening
  const handleHostDropdownToggle = () => {
    if (!showHostDropdown && hostButtonRef.current) {
      const rect = hostButtonRef.current.getBoundingClientRect();
      setHostDropdownPosition({
        top: rect.bottom + 4, // Use viewport coordinates (getBoundingClientRect already accounts for scroll)
        left: rect.left,
        width: rect.width,
      });
    } else {
      setHostDropdownPosition(null);
    }
    setShowHostDropdown(!showHostDropdown);
  };

  // Update dropdown position on scroll/resize
  useEffect(() => {
    if (showHostDropdown && hostButtonRef.current && hostDropdownPosition) {
      const updatePosition = () => {
        const rect = hostButtonRef.current?.getBoundingClientRect();
        if (rect) {
          setHostDropdownPosition({
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width,
          });
        }
      };

      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [showHostDropdown, hostDropdownPosition]);

  // Close host dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        hostButtonRef.current &&
        !hostButtonRef.current.contains(target) &&
        hostDropdownRef.current &&
        !hostDropdownRef.current.contains(target)
      ) {
        setShowHostDropdown(false);
        setHostDropdownPosition(null);
      }
    };

    if (showHostDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      // Also close on scroll (we'll update position instead in the effect above)
      // window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      // window.removeEventListener('scroll', handleScroll, true);
    };
  }, [showHostDropdown]);

  // Load selected host when host_id filter is set externally
  useEffect(() => {
    if (filters.host_id && userRole === "admin" && !selectedHost) {
      const loadSelectedHost = async () => {
        try {
          const hostsData = await usersAPI.getHosts();
          const host = hostsData.find((h) => h.id === filters.host_id);
          if (host) {
            setSelectedHost(host);
          }
        } catch (error) {
          logError("Failed to load selected host", error);
        }
      };
      loadSelectedHost();
    } else if (!filters.host_id && selectedHost) {
      setSelectedHost(null);
    }
  }, [filters.host_id, userRole, selectedHost]);

  // Load date range on component mount
  useEffect(() => {
    const loadDateRange = async () => {
      try {
        setLoadingDateRange(true);
        const range = await eventAPI.getEventDateRange();
        setDateRange(range);
      } catch (error) {
        logError("Failed to load date range", error);
        // Fallback to today's date if API fails
        const today = new Date().toISOString().split("T")[0];
        setDateRange({
          min_date: today,
          max_date: today,
          today: today,
        });
      } finally {
        setLoadingDateRange(false);
      }
    };

    loadDateRange();
  }, []);

  // Prevent form submission at document level for search input
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && e.target === searchInputRef.current) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };

    const handleSubmit = (e: Event) => {
      const submitEvent = e as any; // Type assertion for submit event
      if (
        submitEvent.target &&
        (submitEvent.target as HTMLElement).contains(searchInputRef.current)
      ) {
        submitEvent.preventDefault();
        submitEvent.stopPropagation();
      }
    };

    document.addEventListener(
      "keydown",
      handleKeyDown as unknown as EventListener,
      true
    );
    document.addEventListener(
      "submit",
      handleSubmit as unknown as EventListener,
      true
    );

    return () => {
      document.removeEventListener(
        "keydown",
        handleKeyDown as unknown as EventListener,
        true
      );
      document.removeEventListener(
        "submit",
        handleSubmit as unknown as EventListener,
        true
      );
    };
  }, []);


  // Sync external filters with internal state - but NOT search term
  useEffect(() => {
    // Don't sync if user just changed the status - let their change propagate first
    if (userJustChangedStatus.current) {
      return;
    }

    // Don't sync if user just changed the show past events checkbox - let their change propagate first
    if (userJustChangedPastEvents.current) {
      return;
    }

    // Simple sync - update internal filters
    setFilters(externalFilters);

    // Update selectedStatus to match external filters
    const externalStatusValue = externalFilters.status?.[0] || "";
    if (selectedStatus !== externalStatusValue) {
      setSelectedStatus(externalStatusValue);
    }

    // Only update search term if it's an external change (like clear all filters)
    if (isExternalUpdate.current) {
      setSearchTerm(externalFilters.search || "");
      isExternalUpdate.current = false;
    }
  }, [externalFilters, selectedStatus]);

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Handle show past events checkbox change
  const handleShowPastEventsChange = (checked: boolean) => {
    // Set flag to prevent sync effect from interfering
    userJustChangedPastEvents.current = true;
    
    setShowPastEvents(checked);
    const newFilters = { ...filters };
    
    if (checked) {
      // User wants to see past events - remove date_from filter
      delete newFilters.date_from;
    } else {
      // User wants to hide past events - set date_from to today
      newFilters.date_from = getTodayDate();
    }
    
    // Update local filters
    setFilters(newFilters);
    
    // Notify parent - explicitly set date_from to undefined if we want to show all
    // This ensures the parent knows to remove it
    const filtersForParent = checked 
      ? { ...newFilters, date_from: undefined }
      : newFilters;
    
    onFiltersChange?.(filtersForParent);
    
    // Clear flag after delay to allow change to propagate
    setTimeout(() => {
      userJustChangedPastEvents.current = false;
    }, SYNC_DELAY_MS);
  };

  // Sync showPastEvents state with filters when external filters change
  useEffect(() => {
    // Don't sync if user just changed the checkbox - let their change propagate first
    if (userJustChangedPastEvents.current) {
      return;
    }
    
    if (externalFilters && !isExternalUpdate.current) {
      // Check if date_from is explicitly set
      // - If date_from is not present (undefined), we're showing all events (past and future)
      // - If date_from is set to today, we're hiding past events
      // - If date_from is set to a future date, we're showing events from that date (but checkbox should reflect if we're hiding past)
      const today = getTodayDate();
      const hasDateFrom = externalFilters.date_from !== undefined && externalFilters.date_from !== null;
      // We're hiding past events only if date_from is explicitly set to today
      // If date_from is not set, we're showing all events (checkbox should be checked)
      const isHidingPastEvents = hasDateFrom && externalFilters.date_from === today;
      setShowPastEvents(!isHidingPastEvents);
    }
  }, [externalFilters]);

  // Debounced callback for search filter changes
  const debouncedSearchFilterChange = useDebouncedCallback((trimmedValue: string) => {
    // Only trigger search if value has actually changed
    const currentSearchValue = filters.search || "";

    if (trimmedValue !== currentSearchValue) {
      if (onFiltersChange) {
        const currentFilters = { ...filters };
        currentFilters.search = trimmedValue;
        // Preserve ordering when updating search
        if (filters.ordering) {
          currentFilters.ordering = filters.ordering;
        }
        onFiltersChange(currentFilters);
      }
    }

    setIsSearching(false);
  }, 500);

  // Handle search input change with improved debouncing and focus preservation
  const handleSearchChange = (value: string) => {
    const trimmedValue = value.trim();

    // Update the search term immediately to maintain UI responsiveness
    setSearchTerm(value);

    // For empty searches (after trimming), clear ALL filters immediately
    if (trimmedValue === "") {
      setIsSearching(false);
      // Clear all filters, not just search
      clearFilters();
      return;
    }

    // For single characters, trigger search immediately (no debouncing for first character)
    if (trimmedValue.length === 1) {
      setIsSearching(true);
      if (onFiltersChange) {
        const currentFilters = { ...filters };
        currentFilters.search = trimmedValue;
        // Preserve ordering when updating search
        if (filters.ordering) {
          currentFilters.ordering = filters.ordering;
        }
        onFiltersChange(currentFilters);
      }
      setIsSearching(false);
      return;
    }

    // For longer search terms, use debouncing
    setIsSearching(true);
    debouncedSearchFilterChange(trimmedValue);
  };

  // Maintain focus on search input during re-renders
  useEffect(() => {
    // Store cursor position before any potential re-render
    const cursorPosition = searchInputRef.current?.selectionStart || 0;
    const cursorEnd = searchInputRef.current?.selectionEnd || 0;

    // Only restore focus if we have an active search term
    if (searchInputRef.current && searchTerm && searchTerm.length > 0) {
      // Use multiple strategies to ensure focus is maintained
      const restoreFocus = () => {
        if (searchInputRef.current) {
          const input = searchInputRef.current;

          // Force focus restoration
          input.focus();

          // Restore cursor position
          try {
            input.setSelectionRange(cursorPosition, cursorEnd);
          } catch (e) {
            // Fallback for browsers that don't support setSelectionRange
            input.focus();
          }
        }
      };

      // Immediate focus restoration
      restoreFocus();

      // Backup focus restoration with delay
      const timeoutId = setTimeout(restoreFocus, 10);

      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm, isSearching]);

  // Additional focus management for when the component mounts or filters change
  useEffect(() => {
    if (searchInputRef.current && searchTerm) {
      // Ensure focus is maintained when external filters change
      const input = searchInputRef.current;
      const currentValue = input.value;
      const selectionStart = input.selectionStart || currentValue.length;
      const selectionEnd = input.selectionEnd || currentValue.length;

      input.focus();
      try {
        input.setSelectionRange(selectionStart, selectionEnd);
      } catch (e) {
        input.focus();
      }
    }
  }, [filters, searchTerm]);

  // Handle filter changes
  const handleFilterChange = (key: keyof EventFilters, value: any) => {
    const newFilters = { ...filters };
    if (
      value === "" ||
      value === undefined ||
      value === null ||
      (Array.isArray(value) && value.length === 0)
    ) {
      delete newFilters[key];
    } else {
      newFilters[key] = value;
    }

    setFilters(newFilters);
    onFiltersChange?.(newFilters);
  };

  // Handle date range slider changes
  const handleDateRangeChange = (startDate: string, endDate: string) => {
    const newFilters = { ...filters };

    // Set date_from if a date is provided, otherwise remove it
    if (startDate && startDate.trim() !== "") {
      newFilters.date_from = startDate;
    } else {
      // If no start date, check if we should default to today (hide past events)
      if (!showPastEvents) {
        newFilters.date_from = getTodayDate();
      } else {
        delete newFilters.date_from;
      }
    }

    // Set date_to if a date is provided, otherwise remove it
    if (endDate && endDate.trim() !== "") {
      newFilters.date_to = endDate;
    } else {
      delete newFilters.date_to;
    }

    setFilters(newFilters);
    onFiltersChange?.(newFilters);
  };

  // Handle view mode change
  const handleViewModeChange = (mode: "grid" | "list") => {
    setLocalViewMode(mode);
    onViewModeChange?.(mode);
  };

  // Clear all filters
  const clearFilters = () => {
    isExternalUpdate.current = true; // Flag this as an external update
    const today = getTodayDate();
    setFilters({ date_from: today }); // Reset to default: hide past events
    setSearchTerm("");
    setSelectedHost(null);
    setHostSearchTerm("");
    setShowPastEvents(false); // Reset checkbox to unchecked
    // Call parent's filter change with a special indicator to reset to defaults
    onFiltersChange?.({ _clearAll: true, date_from: today } as any);
  };

  // Clear status filter only (like clear all filters but just for status)
  const clearStatusFilter = () => {
    isExternalUpdate.current = true; // Flag this as an external update
    const clearedFilters = { ...filters };
    delete clearedFilters.status;
    setFilters(clearedFilters);
    setSelectedStatus("");
    // Call parent's filter change with status explicitly set to undefined (no special flag)
    onFiltersChange?.({ ...clearedFilters, status: undefined });
  };

  // Clear category filter only (like clear all filters but just for category)
  const clearCategoryFilter = () => {
    isExternalUpdate.current = true; // Flag this as an external update
    const clearedFilters = { ...filters };
    delete clearedFilters.category_id;
    setFilters(clearedFilters);
    // Call parent's filter change with category_id explicitly set to undefined
    onFiltersChange?.({ ...clearedFilters, category_id: undefined });
  };

  // Clear location filter only (like clear all filters but just for location)
  const clearLocationFilter = () => {
    isExternalUpdate.current = true; // Flag this as an external update
    const clearedFilters = { ...filters };
    delete clearedFilters.location;
    setFilters(clearedFilters);
    // Call parent's filter change with location explicitly set to undefined
    onFiltersChange?.({ ...clearedFilters, location: undefined });
  };

  // Clear host filter only (like clear all filters but just for host)
  const clearHostFilter = () => {
    isExternalUpdate.current = true; // Flag this as an external update
    const clearedFilters = { ...filters };
    delete clearedFilters.host_id;
    setFilters(clearedFilters);
    setSelectedHost(null);
    // Call parent's filter change with host_id explicitly set to undefined
    onFiltersChange?.({ ...clearedFilters, host_id: undefined });
  };

  // Check if any filters are active and count them
  const { hasActiveFilters, activeFilterCount } = useMemo(() => {
    let count = 0;

    // Count search term as a filter if it exists
    if (searchTerm.trim() !== "") {
      count++;
    }

    // System/pagination filters that shouldn't be counted as user filters
    const systemFilters = ["page_size", "ordering", "page"];

    // Count each filter that has a meaningful value, excluding system filters
    Object.entries(filters).forEach(([key, value]) => {
      // Skip system filters
      if (systemFilters.includes(key)) {
        return;
      }

      if (value !== null && value !== undefined && value !== "") {
        // For arrays, check if they have items
        if (Array.isArray(value) && value.length > 0) {
          count++;
        }
        // For strings, check if they're not empty
        else if (typeof value === "string" && value.trim() !== "") {
          count++;
        }
        // For other types (numbers, booleans), count them if they exist
        else if (typeof value !== "string" && !Array.isArray(value)) {
          count++;
        }
      }
    });

    return {
      hasActiveFilters: count > 0,
      activeFilterCount: count,
    };
  }, [filters, searchTerm]);

  // Status options
  const statusOptions: { value: EventStatus; label: string; color: string }[] =
    [
      { value: "pending", label: "Pending Approval", color: "text-orange-600" },
      { value: "active", label: "Active", color: "text-green-600" },
      { value: "rescheduled", label: "Rescheduled", color: "text-yellow-600" },
      { value: "canceled", label: "Cancelled", color: "text-red-600" },
      { value: "rejected", label: "Rejected", color: "text-red-800" },
      { value: "archived", label: "Archived", color: "text-gray-600" },
    ];

  // Pagination component
  const Pagination = () => {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
      const pages = [];
      const maxVisible = 5;

      if (totalPages <= maxVisible) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        const start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        const end = Math.min(totalPages, start + maxVisible - 1);

        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
      }

      return pages;
    };

    return (
      <div
        className={`flex items-center justify-between px-4 py-3 border-t sm:px-6 ${
          theme === "dark"
            ? "bg-gray-800/50 border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        <div className="flex justify-between flex-1 sm:hidden">
          <Button
            variant="outline"
            onClick={() => onPageChange?.(currentPage - 1)}
            disabled={currentPage <= 1}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            onClick={() => onPageChange?.(currentPage + 1)}
            disabled={currentPage >= totalPages}
          >
            Next
          </Button>
        </div>

        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p
              className={`text-sm ${
                theme === "dark" ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Showing{" "}
              <span className="font-medium">
                {(currentPage - 1) * pageSize + 1}
              </span>{" "}
              to{" "}
              <span className="font-medium">
                {Math.min(currentPage * pageSize, totalCount)}
              </span>{" "}
              of <span className="font-medium">{totalCount}</span> results
            </p>
          </div>

          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange?.(currentPage - 1)}
                disabled={currentPage <= 1}
                className="rounded-l-md"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              {getPageNumbers().map((page) => (
                <Button
                  key={page}
                  variant={page === currentPage ? "primary" : "outline"}
                  size="sm"
                  onClick={() => onPageChange?.(page)}
                >
                  {page}
                </Button>
              ))}

              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange?.(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="rounded-r-md"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">
          <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium">Error loading events</p>
          <p className="text-sm text-gray-500 mt-2">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative z-10 max-w-7xl mx-auto px-4 py-6">
      <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1
            className={`text-2xl font-bold ${
              theme === "dark" ? "text-white" : "text-mirsal-black"
            }`}
          >
            Events
          </h1>
          <p
            className={`mt-1 ${
              theme === "dark" ? "text-gray-300" : "text-gray-600"
            }`}
          >
            {totalCount > 0 ? `${totalCount} events found` : "No events found"}
          </p>
        </div>

        <div className="flex items-center space-x-3">
          {/* View Mode Toggle - Hidden on mobile */}
          <div
            className={`hidden md:flex items-center rounded-lg p-1 ${
              theme === "dark" ? "bg-gray-800" : "bg-gray-100"
            }`}
          >
            <Button
              variant={localViewMode === "grid" ? "primary" : "ghost"}
              size="sm"
              onClick={() => handleViewModeChange("grid")}
              className={`${
                localViewMode === "grid"
                  ? theme === "dark"
                    ? "bg-gray-700 shadow-sm"
                    : "bg-white shadow-sm"
                  : ""
              }`}
            >
              <Grid className="h-4 w-4" />
            </Button>
            <Button
              variant={localViewMode === "list" ? "primary" : "ghost"}
              size="sm"
              onClick={() => handleViewModeChange("list")}
              className={`${
                localViewMode === "list"
                  ? theme === "dark"
                    ? "bg-gray-700 shadow-sm"
                    : "bg-white shadow-sm"
                  : ""
              }`}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>

          {showCreateButton && onEventCreate && (
            <Button
              variant="primary"
              onClick={onEventCreate}
              leftIcon={<Plus className="h-4 w-4" />}
            >
              Create Event
            </Button>
          )}
        </div>
      </div>

      {/* Filter Status Indicator */}
      {isFiltering && (
        <div className="flex items-center space-x-2 text-sm">
          <div className="flex items-center space-x-1 text-blue-600">
            <div className="animate-spin rounded-full h-3 w-3 border border-blue-600 border-t-transparent"></div>
            <span>Applying filters...</span>
          </div>
        </div>
      )}

      {/* Search Bar - Full Width */}
      <div className="relative">
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search by event name..."
          value={searchTerm}
          onChange={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSearchChange(e.target.value);
          }}
          onInput={(e) => {
            const target = e.target as HTMLInputElement;
            handleSearchChange(target.value);
          }}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === "Enter") {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          onKeyPress={(e) => {
            e.stopPropagation();
            if (e.key === "Enter") {
              e.preventDefault();
            }
          }}
          onFocus={(e) => {
            e.stopPropagation();
          }}
          onBlur={(e) => {
            const target = e.target as HTMLInputElement;
            // Only clear if the search term was actually changed (not just clicked and blurred)
            // Check if the current searchTerm is different from what's in filters
            const currentSearchInFilters = filters.search || "";
            if (target.value.trim() === "" && currentSearchInFilters !== "") {
              handleSearchChange("");
            }
          }}
          className={`block w-full rounded-md shadow-sm focus:ring-mirsal-green focus:border-mirsal-green sm:text-sm transition-all duration-200 pl-10 pr-3 py-2.5 ${
            theme === "dark"
              ? `border-gray-600 bg-gray-800 text-white placeholder-gray-400 ${
                  isSearching ? "bg-gray-700" : ""
                }`
              : `border-gray-300 bg-white text-gray-900 placeholder-gray-500 ${
                  isSearching ? "bg-gray-50" : ""
                }`
          }`}
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck="false"
        />
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search
            className={`h-4 w-4 ${
              theme === "dark" ? "text-gray-400" : "text-gray-400"
            }`}
          />
        </div>
        {isSearching && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-mirsal-green"></div>
          </div>
        )}
      </div>

      {/* Main Layout: Events Grid + Right Sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Main Content - Events Grid/List in Card */}
        <div className="lg:col-span-9">
          <div className={`rounded-lg p-2 backdrop-blur-sm ${
            theme === 'dark' 
              ? 'bg-gray-900/80 border border-gray-700' 
              : 'bg-white/90 border border-gray-200'
          }`}>
            {/* Events Grid/List */}
            {events.length === 0 ? (
            <div className="text-center py-12">
              <Calendar
                className={`h-12 w-12 mx-auto mb-4 ${
                  theme === "dark" ? "text-gray-400" : "text-gray-400"
                }`}
              />
              <h3
                className={`text-lg font-medium mb-2 ${
                  theme === "dark" ? "text-white" : "text-gray-900"
                }`}
              >
                No events found
              </h3>
              <p
                className={`mb-6 ${
                  theme === "dark" ? "text-gray-300" : "text-gray-500"
                }`}
              >
                {hasActiveFilters
                  ? "Try adjusting your filters or search terms."
                  : "Get started by creating your first event."}
              </p>
              {showCreateButton && onEventCreate && !hasActiveFilters && (
                <Button
                  variant="primary"
                  onClick={onEventCreate}
                  leftIcon={<Plus className="h-4 w-4" />}
                >
                  Create Your First Event
                </Button>
              )}
            </div>
          ) : (
            <>
              <div
                className={`relative w-full ${
                  localViewMode === "grid"
                    ? "grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4 items-stretch"
                    : "space-y-3"
                }`}
              >
                {/* Loading Overlay */}
                {(loading || isFiltering) && (
                  <div
                    className={`absolute inset-0 z-10 flex items-center justify-center rounded-lg ${
                      isFiltering
                        ? "bg-white/30 backdrop-blur-[1px]"
                        : "bg-white/70 backdrop-blur-sm"
                    }`}
                  >
                    <div className="flex flex-col items-center space-y-2">
                      <div
                        className={`animate-spin rounded-full border-b-2 border-mirsal-green ${
                          isFiltering ? "h-6 w-6" : "h-8 w-8"
                        }`}
                      ></div>
                      <p
                        className={`text-gray-600 ${
                          isFiltering ? "text-xs" : "text-sm"
                        }`}
                      >
                        {isFiltering ? "Filtering..." : "Loading events..."}
                      </p>
                    </div>
                  </div>
                )}
                {events.map((event) => (
                  <EventCard
                    key={event.id}
                    event={event}
                    onView={onEventView}
                    onEdit={onEventEdit}
                    onDelete={onEventDelete}
                    onManageGuests={onManageGuests}
                    onApprove={onApprove}
                    onReject={onReject}
                    userRole={userRole}
                    compact={localViewMode === "list"}
                  />
                ))}
              </div>

              {/* Pagination */}
              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Pagination />
              </div>
            </>
          )}
          </div>
        </div>

        {/* Right Sidebar - Filters & Sort Card */}
        <div className="lg:col-span-3">
          <div className="sticky top-6 space-y-4">
            <div className={`rounded-lg border-2 p-4 ${
              theme === "dark" 
                ? "bg-gray-800 border-gray-700" 
                : "bg-white border-gray-200"
            }`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${
                  theme === "dark" ? "text-white" : "text-gray-900"
                }`}>
                  Filters & Sort
                </h3>
                {hasActiveFilters && (
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    theme === "dark" 
                      ? "bg-mirsal-green/20 text-mirsal-green" 
                      : "bg-mirsal-green/10 text-mirsal-green"
                  }`}>
                    {activeFilterCount} active
                  </span>
                )}
              </div>

              <div className="space-y-4">
                {/* Sort Dropdown */}
                <div>
                  <Select
                    label="Sort by"
                    options={[
                      { value: '', label: 'Sort By..' },
                      { value: '-event_date', label: 'Event Date (Upcoming First)' },
                      { value: 'event_date', label: 'Event Date (Oldest First)' },
                      { value: '-accepted_guest_count', label: 'Most Guests' },
                      { value: 'accepted_guest_count', label: 'Least Guests' },
                      { value: 'title', label: 'Title (A-Z)' },
                      { value: '-title', label: 'Title (Z-A)' },
                      { value: '-created_at', label: 'Newest Created' },
                      { value: 'created_at', label: 'Oldest Created' },
                      { value: 'event_time', label: 'Time (Morning First)' },
                      { value: '-event_time', label: 'Time (Evening First)' },
                      { value: 'status', label: 'Status (A-Z)' },
                      { value: '-status', label: 'Status (Z-A)' },
                      { value: 'location', label: 'Location (A-Z)' },
                      { value: '-location', label: 'Location (Z-A)' },
                    ]}
                    value={filters.ordering || ''}
                    onChange={(value) => {
                      if (value === '') {
                        // Clear ordering - explicitly set to undefined so the hook knows to remove it
                        const newFilters = { ...filters, ordering: undefined };
                        setFilters(newFilters);
                        // Pass ordering as undefined explicitly to parent so it knows to clear it
                        onFiltersChange?.(newFilters);
                      } else {
                        handleFilterChange('ordering', value);
                      }
                    }}
                    placeholder="Sort By.."
                  />
                </div>

                {/* Status Filter */}
                <div>
                  <Select
                    label="Status"
                    options={[
                      { value: "", label: "All statuses" },
                      ...statusOptions.map((status) => ({
                        value: status.value,
                        label: status.label,
                      })),
                    ]}
                    value={selectedStatus}
                    onChange={(value) => {
                      userJustChangedStatus.current = true;
                      setSelectedStatus(value);
                      setTimeout(() => {
                        userJustChangedStatus.current = false;
                      }, STATUS_CHANGE_DELAY_MS);

                      if (value === "") {
                        clearStatusFilter();
                      } else {
                        const newFilters = {
                          ...filters,
                          status: [value as EventStatus],
                        };
                        setFilters(newFilters);
                        onFiltersChange?.(newFilters);
                      }
                    }}
                    placeholder="All statuses"
                  />
                </div>

                {/* Category Filter */}
                <div>
                  <Select
                    label="Category"
                    options={[
                      { value: "", label: "All categories" },
                      ...categories.map((category) => ({
                        value: category.id.toString(),
                        label: category.name,
                      })),
                    ]}
                    value={filters.category_id?.toString() || ""}
                    onChange={(value) => {
                      if (value === "") {
                        clearCategoryFilter();
                      } else {
                        handleFilterChange("category_id", parseInt(value));
                      }
                    }}
                    placeholder="All categories"
                  />
                </div>

                {/* Location Filter */}
                <div>
                  <Select
                    label="Location"
                    options={[
                      { value: "", label: "All locations" },
                      ...locations.map((location) => ({
                        value: location,
                        label: location,
                      })),
                    ]}
                    value={filters.location || ""}
                    onChange={(value) => {
                      if (value === "") {
                        clearLocationFilter();
                      } else {
                        handleFilterChange("location", value);
                      }
                    }}
                    placeholder="All locations"
                    loading={loadingLocations}
                    disabled={loadingLocations}
                  />
                </div>

                {/* Host Filter - Admin Only */}
                {userRole === "admin" && (
                  <div className="relative">
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        theme === "dark" ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Host
                    </label>
                    <div className="relative">
                      <button
                        ref={hostButtonRef}
                        type="button"
                        onClick={handleHostDropdownToggle}
                        className={`w-full rounded-md border shadow-sm focus:ring-mirsal-green focus:border-mirsal-green sm:text-sm transition-all duration-200 pl-3 pr-10 py-2 text-left ${
                          theme === "dark"
                            ? "border-gray-600 bg-gray-800 text-white"
                            : "border-gray-300 bg-white text-gray-900"
                        } ${selectedHost ? "" : "text-gray-500"}`}
                      >
                        {selectedHost ? selectedHost.get_full_name : "All hosts"}
                      </button>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <Users
                          className={`h-4 w-4 ${
                            theme === "dark" ? "text-gray-400" : "text-gray-400"
                          }`}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Date Range Picker */}
                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${
                      theme === "dark" ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    Date Range
                  </label>
                  {dateRange && !loadingDateRange ? (
                    <div className="w-full [&>div>div]:grid-cols-1">
                      <DateRangePicker
                        minDate={(() => {
                          const tenYearsAgo = new Date();
                          tenYearsAgo.setFullYear(tenYearsAgo.getFullYear() - 10);
                          return tenYearsAgo.toISOString().split("T")[0];
                        })()}
                        maxDate={(() => {
                          const tenYearsAhead = new Date();
                          tenYearsAhead.setFullYear(
                            tenYearsAhead.getFullYear() + 10
                          );
                          return tenYearsAhead.toISOString().split("T")[0];
                        })()}
                        startDate={filters.date_from}
                        endDate={filters.date_to}
                        onChange={handleDateRangeChange}
                        className="w-full"
                      />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-4">
                      <LoadingSpinner size="sm" />
                      <span className="ml-2 text-sm text-gray-500">
                        Loading...
                      </span>
                    </div>
                  )}
                </div>

                {/* Show Past Events Checkbox */}
                <div className="flex items-center">
                  <label 
                    className="flex items-center space-x-2 cursor-pointer"
                    onClick={(e) => {
                      // Prevent any event bubbling that might interfere
                      e.stopPropagation();
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={showPastEvents}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleShowPastEventsChange(e.target.checked);
                      }}
                      onClick={(e) => {
                        // Prevent event from bubbling up
                        e.stopPropagation();
                      }}
                      className={`w-4 h-4 rounded border-2 focus:ring-2 focus:ring-mirsal-green ${
                        theme === "dark"
                          ? "bg-gray-800 border-gray-600 text-mirsal-green focus:ring-mirsal-green"
                          : "bg-white border-gray-300 text-mirsal-green focus:ring-mirsal-green"
                      }`}
                    />
                    <span
                      className={`text-sm font-medium ${
                        theme === "dark" ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Show past events
                    </span>
                  </label>
                </div>

                {/* Clear Filters */}
                <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    disabled={!hasActiveFilters}
                    className={`w-full transition-all duration-200 font-medium ${
                      hasActiveFilters
                        ? "text-mirsal-green hover:text-mirsal-green-dark hover:bg-mirsal-green/10"
                        : "text-gray-400 dark:text-gray-500 cursor-not-allowed"
                    }`}
                  >
                    Clear all filters
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Host Dropdown Portal - Renders outside container */}
      {showHostDropdown &&
        hostDropdownPosition &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={hostDropdownRef}
            className={`fixed z-[9999] rounded-md shadow-lg ${
              theme === "dark"
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-200"
            } border max-h-60 overflow-hidden`}
            style={{
              top: `${hostDropdownPosition.top}px`,
              left: `${hostDropdownPosition.left}px`,
              width: `${hostDropdownPosition.width}px`,
            }}
          >
            {/* Search input inside dropdown */}
            <div className="p-2 border-b border-gray-200 dark:border-gray-700">
              <input
                type="text"
                placeholder="Search hosts..."
                value={hostSearchTerm}
                onChange={(e) => handleHostSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className={`w-full rounded-md px-3 py-2 text-sm ${
                  theme === "dark"
                    ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                } border focus:ring-mirsal-green focus:border-mirsal-green`}
                autoFocus
              />
            </div>

            {/* Host list */}
            <div
              className="max-h-48 overflow-y-auto host-dropdown-scroll"
              style={{
                scrollbarWidth: "thin",
                scrollbarColor:
                  theme === "dark" ? "#4B5563 #1F2937" : "#D1D5DB #F3F4F6",
              }}
            >
              <style>{`
              .host-dropdown-scroll::-webkit-scrollbar {
                width: 8px;
              }
              .host-dropdown-scroll::-webkit-scrollbar-track {
                background: ${theme === "dark" ? "#1F2937" : "#F3F4F6"};
                border-radius: 4px;
              }
              .host-dropdown-scroll::-webkit-scrollbar-thumb {
                background: ${theme === "dark" ? "#4B5563" : "#D1D5DB"};
                border-radius: 4px;
              }
              .host-dropdown-scroll::-webkit-scrollbar-thumb:hover {
                background: ${theme === "dark" ? "#6B7280" : "#9CA3AF"};
              }
            `}</style>
              {loadingHosts ? (
                <div className="p-4 text-center">
                  <LoadingSpinner size="sm" />
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => handleHostSelect(null)}
                    className={`w-full text-left px-4 py-2 text-sm ${
                      !selectedHost
                        ? "bg-mirsal-green/10 text-mirsal-green hover:bg-mirsal-green/20 dark:hover:bg-mirsal-green/20"
                        : theme === "dark"
                        ? "text-white hover:bg-gray-700"
                        : "text-gray-900 hover:bg-gray-100"
                    }`}
                  >
                    All hosts
                  </button>
                  {hosts.length === 0 ? (
                    <div
                      className={`px-4 py-2 text-sm ${
                        theme === "dark" ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      No hosts found
                    </div>
                  ) : (
                    hosts.map((host) => (
                      <button
                        key={host.id}
                        type="button"
                        onClick={() => handleHostSelect(host)}
                        className={`w-full text-left px-4 py-2 text-sm ${
                          selectedHost?.id === host.id
                            ? "bg-mirsal-green/10 text-mirsal-green hover:bg-mirsal-green/20 dark:hover:bg-mirsal-green/20"
                            : theme === "dark"
                            ? "text-white hover:bg-gray-700"
                            : "text-gray-900 hover:bg-gray-100"
                        }`}
                      >
                        {host.get_full_name}
                      </button>
                    ))
                  )}
                </>
              )}
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

export default EventList;
